import React from 'react';
import type { LocalDate } from '@js-joda/core';
import { showSubscriptionStatus } from '../Data/Subscription';
import { DataEntitiesSubscriptionStatus } from '../shared/api/axios-client';

/** Bootstrap bg-* class for the status dot: success=green, warning=orange, danger=red, secondary=gray */
function statusLabelToDotClass(label: string): string {
  switch (label) {
    case 'Active':
      return 'bg-success';
    case 'Pending':
      return 'bg-warning';
    case 'Cancelled':
      return 'bg-danger';
    case 'Inactive':
      return 'bg-secondary';
    default:
      return 'bg-secondary'; // Future, Waitlisted, Unknown Status
  }
}

/** Very subtle pill background: tint of the dot color (low % so it stays very light) */
function statusLabelToPillBgStyle(label: string): React.CSSProperties {
  const mix = '6%'; // very subtle
  const varMap: Record<string, string> = {
    Active: 'var(--bs-success)',
    Pending: 'var(--bs-warning)',
    Cancelled: 'var(--bs-danger)',
    Inactive: 'var(--bs-secondary)',
  };
  const token = varMap[label] ?? 'var(--bs-secondary)';
  return { backgroundColor: `color-mix(in srgb, ${token} ${mix}, white)` };
}

export interface SubscriptionStatusPillProps {
  /** When provided, label is derived from status (and optional startDate). */
  status?: DataEntitiesSubscriptionStatus;
  /** Optional start date for "Future" when start is after today. */
  startDate?: string | LocalDate;
  /** When provided, use this label and its color instead of computing from status. */
  displayLabel?: string;
  /** When true, show subtle accent background and border. When false (default), no background or border. */
  accent?: boolean;
  className?: string;
}

export function SubscriptionStatusPill({
  status,
  startDate,
  displayLabel,
  accent = false,
  className = '',
}: SubscriptionStatusPillProps) {
  const label = displayLabel ?? (status != null ? showSubscriptionStatus(status, startDate) : '');
  const dotClass = statusLabelToDotClass(label);
  const pillBgStyle = accent ? statusLabelToPillBgStyle(label) : undefined;

  return (
    <span
      className={`d-inline-flex align-items-center gap-2 rounded-pill px-2 py-1 small ${accent ? 'border' : ''} ${className}`.trim()}
      style={{ color: 'var(--bs-body-color)', ...(pillBgStyle ?? {}) }}
    >
      <span
        className={`rounded-circle ${dotClass}`}
        style={{ width: 8, height: 8 }}
        aria-hidden
      />
      {label}
    </span>
  );
}
